<template>
  <div class="base-pill-group" :class="{ 'base-pill-group--is-small': isSmall }">
    <button
      v-for="opt in options"
      :key="String(opt.value)"
      type="button"
      class="base-pill-group__pill"
      :class="{ 'is-active': isActive(opt.value) }"
      :aria-pressed="isActive(opt.value) ? 'true' : 'false'"
      @click="select(opt.value)"
    >
      {{ opt.label }}
    </button>
  </div>
</template>

<script>
/**
 * Pill group for compact exclusive-or-toggled choices ("Tous | Anglais | Français",
 * "Opportunité | Soumission"). Single-select by default: `value` is one option value and clicking
 * a pill emits `input` with it. With `multiple`, `value` is an array and clicking a pill emits
 * `input` with the toggled array.
 */
export default {
  name: 'BasePillGroup',
  props: {
    // [{ value: any, label: string }] — values are compared with strict equality.
    options: {
      type: Array,
      required: true,
    },
    // Selected option value; an array of values when `multiple`.
    value: {
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    isSmall: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    isActive(value) {
      if (this.multiple) return Array.isArray(this.value) && this.value.includes(value);
      return value === this.value;
    },
    select(value) {
      if (this.multiple) {
        const current = Array.isArray(this.value) ? this.value : [];
        this.$emit('input', this.isActive(value) ? current.filter((v) => v !== value) : [...current, value]);
      } else if (value !== this.value) {
        this.$emit('input', value);
      }
    },
  },
};
</script>

<style lang="scss">
.base-pill-group {
  display: flex;
  gap: 6px;

  &__pill {
    padding: 4px 16px;
    border-radius: $radius-full;
    border: 1px solid $color-border;
    background: $color-white;
    color: $color-normal-pale;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 150ms ease, color 150ms ease, border-color 150ms ease;

    &:hover {
      color: $color-accent;
      border-color: $color-accent;
    }

    &:focus-visible {
      box-shadow: $shadow-focus-outline;
      outline: none;
    }

    &.is-active {
      background: $color-accent;
      border-color: $color-accent;
      color: $color-white;
    }
  }

  &--is-small .base-pill-group__pill {
    padding: 3px 14px;
    font-size: 12px;
  }

  // Phones: 32px pills, as chips, whose touch area reaches 44px (docs/page-layout.md); pills that
  // don't fit the line go on the next, far enough below for the areas not to overlap.
  @include screen-below('tablet') {
    flex-wrap: wrap;
    row-gap: 12px;

    .base-pill-group__pill {
      min-height: 32px;
      font-size: 14px;
      @include phone-touch-area;
    }
  }
}
</style>
